"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { cn } from "@shared/src/utils/cn";

interface CustomCheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  className?: string;
  variant?: "default" | "radio";
}

const CustomCheckbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CustomCheckboxProps>(
  ({ className, variant = "default", ...props }, ref) => {
    const variants = {
      default: {
        root: "rounded-xl data-[state=checked]:bg-surfaces-bg-primaryinverse",
        indicator: "text-text-primary data-[state=checked]:text-text-primaryinverse",
        icon: <Check className="size-5 p-0.5" />,
      },
      radio: {
        root: "rounded-full data-[state=checked]:bg-checkboxes-checkbox-checked-bg",
        indicator: "text-text-primaryinverse",
        icon: <div className="h-2 w-2 rounded-full bg-surfaces-bg-primaryinverse" />,
      },
    };

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          "peer h-5 w-5 shrink-0 border border-border-tertiary bg-surfaces-bg-primary",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-tertiary",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "transition-all duration-200",
          variants[variant].root,
          className,
        )}
        {...props}
      >
        <CheckboxPrimitive.Indicator className={cn("flex items-center justify-center", variants[variant].indicator)}>
          {variants[variant].icon}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  },
);

CustomCheckbox.displayName = "CustomCheckbox";

export { CustomCheckbox };
